import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Paperclip, ChevronLeft, ChevronRight, Wallet, Camera, BarChart3, MessageSquareText, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import api from "@/lib/api";
import { Card, PageHeader, Button, Badge } from "@/components/ui-kit";

/**
 * Iter v32 — Karyawan-side read-only debt history (/karyawan/hutang).
 */

const fmtIDR = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDT = (ts) => { try { return new Date(ts).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ts || ""; } };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function KaryawanHutang() {
  const [data, setData] = useState({ rows: [], total: 0, page: 1, total_pages: 1, balances: {} });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/employee-debts", { params: { page, per_page: 20 } }); setData(r.data); }
    catch { /* */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const myBalance = useMemo(() => {
    const ids = Object.keys(data.balances || {});
    return ids.length ? data.balances[ids[0]] : 0;
  }, [data.balances]);

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 lg:p-8 space-y-4" data-testid="karyawan-hutang-page">
      <PageHeader overline="Karyawan" title="Hutang Saya"
        description="Riwayat hutang dan pembayaran Anda — hanya admin/finance yang dapat menambah atau mengubah." />

      <div className="flex flex-wrap gap-2">
        <Link to="/karyawan/absen" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-white border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-stone-50"><Camera className="h-3.5 w-3.5" /> Absen</Link>
        <Link to="/karyawan/ringkasan" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-white border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-stone-50"><BarChart3 className="h-3.5 w-3.5" /> Ringkasan</Link>
        <Link to="/karyawan/chat-hr" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-white border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-stone-50"><MessageSquareText className="h-3.5 w-3.5" /> Chat HR</Link>
        <Link to="/karyawan/hutang" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs rounded-full bg-stone-900 text-white"><Wallet className="h-3.5 w-3.5" /> Hutang Saya</Link>
      </div>

      <Card className={`p-5 ${myBalance > 0 ? "bg-red-50 border-red-200" : myBalance < 0 ? "bg-emerald-50 border-emerald-200" : "bg-stone-50"}`}>
        <div className="text-[10px] uppercase tracking-widest text-stone-500">Saldo Hutang Saat Ini</div>
        <div className={`text-3xl font-display font-bold mt-1 font-mono ${myBalance > 0 ? "text-red-700" : myBalance < 0 ? "text-emerald-700" : "text-stone-700"}`} data-testid="karyawan-hutang-balance">
          {fmtIDR(myBalance)}
        </div>
        <p className="text-xs text-stone-600 mt-1">
          {myBalance > 0 ? "Anda masih memiliki hutang dengan perusahaan." : myBalance < 0 ? "Anda memiliki saldo lebih (credit)." : "Tidak ada hutang."}
        </p>
      </Card>

      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
            <tr>
              <th className="text-left px-3 py-3">Waktu</th>
              <th className="text-center px-3 py-3">Tipe</th>
              <th className="text-right px-3 py-3">Jumlah</th>
              <th className="text-left px-3 py-3">Catatan</th>
              <th className="text-center px-3 py-3">Bukti</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading && <tr><td colSpan={5} className="px-3 py-12 text-center text-xs text-stone-400">Memuat…</td></tr>}
            {!loading && data.rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-12 text-center text-xs text-stone-400">Tidak ada catatan hutang.</td></tr>
            )}
            {!loading && data.rows.map((r) => (
              <tr key={r.id} className="hover:bg-stone-50/40" data-testid={`karyawan-hutang-row-${r.id}`}>
                <td className="px-3 py-2.5 font-mono text-xs">{fmtDT(r.ts)}</td>
                <td className="px-3 py-2.5 text-center">
                  <Badge tone={r.type === "debt" ? "red" : "emerald"}>
                    {r.type === "debt" ? <><ArrowUpCircle className="h-3 w-3 inline" /> hutang</> : <><ArrowDownCircle className="h-3 w-3 inline" /> bayar</>}
                  </Badge>
                </td>
                <td className={`px-3 py-2.5 text-right font-mono font-bold ${r.type === "debt" ? "text-red-700" : "text-emerald-700"}`}>{fmtIDR(r.amount)}</td>
                <td className="px-3 py-2.5 text-xs text-stone-600">{r.note || "—"}</td>
                <td className="px-3 py-2.5 text-center">
                  {r.has_attachment
                    ? <a href={`${BACKEND}/api/employee-debts/${r.id}/attachment`} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-xs">
                        <Paperclip className="h-3.5 w-3.5" /> lihat
                      </a>
                    : <span className="text-stone-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.total_pages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-stone-100 text-xs text-stone-600">
            <span>Halaman {data.page} dari {data.total_pages} · Total {data.total} catatan</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="px-2 py-1 border border-stone-300 hover:bg-stone-50 disabled:opacity-40"><ChevronLeft className="h-3.5 w-3.5" /></button>
              <button onClick={() => setPage(Math.min(data.total_pages, page + 1))} disabled={page >= data.total_pages} className="px-2 py-1 border border-stone-300 hover:bg-stone-50 disabled:opacity-40"><ChevronRight className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
